/// <reference path="../../apps/typings/jquery-validation.d.ts"/>
/// <reference path="../models/generated/MitgliedschaftMglCheckExternResponseApi.d.ts"/>
/// <reference path="../models/generated/MitgliedschaftMglVesCheckExternResponseApi.d.ts"/>
import { BasicErrorService } from './BasicErrorService';
import { BasicApplicationUrlBuilder } from './BasicApplicationUrlBuilder';
import { AppUtils } from '../../shared/utils/index';

/**
 * Performs form validation based on the validatorRules and validationmessages from the app options.
 * For all fields to validate an according validation rule must be provided in the default options in app.js.
 */
export class BasicValidationService<T extends BasicAppDefinition> {
	public static readonly VALIDATION_FAILED_MARKER = 'validation-failed';
	public static readonly VALIDATION_FAILED_ATTR = `data-${BasicValidationService.VALIDATION_FAILED_MARKER}`;
	public static readonly BACKEND_VALIDATION_MARKER = 'backend-validation';
	public static readonly BACKEND_VALIDATION_ATTR = `data-${BasicValidationService.BACKEND_VALIDATION_MARKER}`;

	protected readonly lookupServerFieldnames: { [key: string]: string };
	protected readonly errorService: BasicErrorService<T>;
	protected readonly $ctx: JQuery;
	protected readonly validators: (JQueryValidation.Validator & { currentForm: HTMLElement, lastActive: HTMLElement, check: (elm: JQuery | HTMLElement) => boolean })[];
	protected readonly options: BasicAppOptions<T>;
	protected readonly callback: () => void;
	protected readonly forms: JQuery<HTMLFormElement>;
	protected readonly validationOptions: ValidationRules;
	protected readonly urlBuilder: BasicApplicationUrlBuilder<T>;
	protected scrollTopFooterHeight: number;
	protected ignoreValidationSelector: string;
	protected ignoreValidationComposite: string;
	protected errorClassName: string;
	protected errorClassSelector: string;
	protected rowClass: string;
	protected noCheckMarkFieldTypes: string;
	protected calendarSelector: string;

	protected isFormSubmit = false;

	constructor(app: { options: BasicAppOptions<T> }, forms: JQuery, cb: () => void = () => { /* ignore by default */ }) {
		this.forms = $(forms) as JQuery<HTMLFormElement>;
		this.callback = cb;
		this.options = app.options;
		this.validationOptions = this.options.app.validatorRules;
		this.validationOptions.messages = this.options.app.validationmessages.client;
		this.validators = [];
		this.$ctx = this.options.$ctx;
		this.errorService = new BasicErrorService(app.options);
		this.lookupServerFieldnames = {} as { [key: string]: string };
		this.ignoreValidationSelector = '.ignore-validation';
		this.ignoreValidationComposite = `:hidden, ${this.ignoreValidationSelector}`;
		this.fillLookupServerFieldnames();
		this.scrollTopFooterHeight = 80;
		this.errorClassName = 'll-error-msg mm-error-msg';
		this.errorClassSelector = '.ll-error-msg, .mm-error-msg';
		this.rowClass = '.js-row, .ll-row, .mm-row';
		this.noCheckMarkFieldTypes = '[type=radio], [type=checkbox], [type=file]';
		this.calendarSelector = '.a-basic-input-text--calendar';

		this.urlBuilder = new BasicApplicationUrlBuilder(app.options);
		this.init();
	}

	protected init(): void {
		this.initFormValidation();
	}

	public updateValidationMessages(): void {
		if (this.forms.length > 0) {
			this.addValidationMessages();
		}
	}

	/**
	 * Registers form validation on submit click. The submit link must have the class '.js-submit-form'.
	 */
	private registerSubmitListener(): void {
		this.forms.on('click', '.js-submit-form', this.onHandleFormSubmit.bind(this));
	}

	/**
	 * Performs the form validation on form submit. If validation fails, the submit / click is not performed.
	 */
	protected onHandleFormSubmit(event: JQuery.TriggeredEvent): void {
		if (!this.prepareFormSubmit(true, $(event.currentTarget).parents('form'))) {
			event.preventDefault();
		}
	}

	/**
	 * Performs the form validation.
	 *
	 * @return true, if the form validation was successful, false otherwise.
	 */
	public isValid(): boolean {
		this.isFormSubmit = true;
		//write as a body property to share this information between app and subapp
		(document.body as any).isFormSubmit = true;
		let result = true;
		this.forms.each(function (index: number, element: HTMLFormElement) {
			if (!$(element).valid()) {
				result = false;
			}
		});
		(document.body as any).isFormSubmit = false;
		this.isFormSubmit = false;
		return result;
	}

	public isValidWithoutTriggerErrors(): boolean {
		this.isFormSubmit = true;
		let result = true;
		this.forms.each(function (index: number, element: HTMLFormElement) {
			if (!$(element).validate().checkForm()) {
				result = false;
			}
		});
		this.isFormSubmit = false;
		return result;
	}

	/**
	 * Prepares form data for submission, including validation (if requested) and transmission of tracking data to enabled provider.
	 * @param doValidate - if true, triggers input validation
	 * @param forms - optional forms collection to do the work, if not specified this._forms is taken
	 */
	protected prepareFormSubmit(doValidate: boolean, forms: JQuery): boolean {
		const formElements: JQuery<HTMLFormElement> = forms as JQuery<HTMLFormElement> || this.forms;
		if (doValidate && !formElements.valid()) {
			return false;
		}
		return true;
	}

	/**
	 * function to handle the client-side validation. The validation messages of the options
	 * are extended and the event handlers are initialized.
	 */
	protected initFormValidation(): void {
		this.addCustomMethods();

		this.forms.each((index, element) => {

			element.validation = $(element).validate({
				errorClass: 'is-error',
				focusInvalid: false,
				invalidHandler: (form, validator) => {
					if (!validator.numberOfInvalids()) {
						return;
					}
					AppUtils.View.scrollToElement(validator.errorList[0].element, this.scrollTopFooterHeight);
				},
				errorPlacement: this.generalErrorPlacement.bind(this),
				onkeyup: false,
				onfocusout: this.decorateElement.bind(this),
				onfocusin: this.undecorateElement.bind(this),
				onclick: false,
				ignore: this.ignoreValidationComposite
			});
			this.validators.push(element.validation);
		});


		if (this.forms.length > 0 && this.forms[0]) {
			this.addValidationMessages();
			this.registerSubmitListener();
		}

		this.showGeneralValidationMessage();

		if (this.callback) {
			this.callback();
		}
	}

	protected decorateSingleElement(element: JQuery | HTMLElement | string | JQuery.PlainObject, _event?: JQuery.Event): void { //NOSONAR S4323 suggests nonsense, there is no meaningful type alias here
		const $element = $(element);
		this.setValidationMarkers($element, $element.valid());
	}

	protected decorateElement(element: JQuery | HTMLElement | string | JQuery.PlainObject, event?: JQuery.Event): void { //NOSONAR S4323 suggests nonsense, there is no meaningful type alias here
		const $element = $(element);
		const groupMark = $element.data('validation-group');
		if (groupMark) {
			this.$ctx.find(`[data-validation-group="${groupMark}"]`).each((index, groupElement) => {
				this.decorateSingleElement(groupElement, event);
			});
		} else {
			this.decorateSingleElement(element, event);
		}
	}

	protected undecorateElement(element: HTMLElement | string | JQuery.PlainObject, event?: JQuery.Event): void { //NOSONAR S4323 suggests nonsense, there is no meaningful type alias here
		if (!event || "Enter" !== event.key) {
			const $elm = $(element);
			$elm.removeClass('is-error');
			let $parent = $elm.parents(this.rowClass);
			if ($parent.length > 1) {
				$parent = $parent.first();
			}

			const shouldUndecorateParent = $elm.data('undecorate-parent') ?? true;
			if (shouldUndecorateParent) {
				$parent.removeClass('is-error');
			}
		}
	}

	protected generalErrorPlacement(error: JQuery, element: JQuery): void {
		const $element = $(element);
		if ($element.data("customerrors")) {
			return;
		}
		const $parent = $element.parents(this.rowClass).first(),
			errorElement = $parent.find(this.errorClassSelector);
		$element.parents(this.rowClass).first().removeClass('is-valid').addClass('is-error');
		this.setCheckMarkOnField($element, $parent);

		if (errorElement.is(':visible')) {
			errorElement.html(error[0].innerHTML);
		} else {
			const $before = $element.prev('input').is(':visible') ? $element.prev() : $element.parent();
			$(`<div class="${this.errorClassName}" role="alert">`).html(error[0].innerHTML).insertBefore($before);
		}
	}

	protected setValidationMarkers(field: JQuery, isValid = true, row?: JQuery): void {
		const $row = (row || field.parents(this.rowClass)).first();
		$row.removeClass(isValid ? 'is-error' : 'is-valid').addClass(isValid ? 'is-valid' : 'is-error');
		if (field.data(BasicValidationService.VALIDATION_FAILED_MARKER) === isValid) {
			field.removeClass(isValid ? 'is-error' : 'valid').addClass(isValid ? 'valid' : 'is-error');
		}
		this.setCheckMarkOnField(field, $row);
	}

	protected setCheckMarkOnField(field: JQuery, row: JQuery): void {
		if (!field.is(this.noCheckMarkFieldTypes)) {
			row.addClass('is-check');
		}
	}

	/**
	 * Show for every callback-entry with CanceledOnValidation=true a error message at the according Field.
	 * Unassigned callback-entries are shown as a general error message at the top of the form
	 */
	public showServerValidationErrors(jqxhr: JQueryXHR): void {
		let fieldErrorset = false;
		this.showGeneralValidationMessage();

		if (jqxhr && jqxhr.responseJSON && jqxhr.responseJSON.Messages) {
			const messages = (jqxhr.responseJSON.Messages as AdacResultMessage[]).slice().reverse();
			messages.forEach((item) => {
				const errorSource = item.Source;
				if (item.CancelledOnValidation) {
					const fieldName = errorSource.replace(/.+\.(Anschrift|AbwKontoInhaber)$/, "$1");
					if (fieldName !== errorSource && fieldName in this.lookupServerFieldnames
						&& this.$ctx.find(`#${this.lookupServerFieldnames[fieldName]}`).length) {
						this.setServerValidationFlag(false, this.lookupServerFieldnames[fieldName]);
						fieldErrorset = true;
					}
				} else if (errorSource === "Identity" && this.$ctx.find(`#${this.lookupServerFieldnames[errorSource]}`).length) {
					this.setServerValidationFlag(false, this.lookupServerFieldnames[errorSource]);
					fieldErrorset = true;
				}
			});
		}

		if (!fieldErrorset && this.validationOptions.messages['customflagvalidation']) {
			this.showGeneralValidationMessage(this.validationOptions.messages['customflagvalidation']);
		}
		AppUtils.View.scrollTo(0);
	}

	protected showGeneralValidationMessage(message?: string): void {
		const $generalErrorElement = this.$ctx.find('.js-general');
		$generalErrorElement.html(message || "");
		AppUtils.View.hideElement($generalErrorElement, !message);
	}

	/**
	 * Sets a validation flag for the provided element that is recognized by the validation service.
	 * Can be used to consider server side validations in the client side validation.
	 */
	public setServerValidationFlag(success: boolean, elementIdentifier: string): void {
		const $field = this.findValidatingElement(elementIdentifier);
		$field.data(BasicValidationService.VALIDATION_FAILED_MARKER, !success);
		if (success) {
			this.setValidationMarkers($field);
		}
	}

	/**
	 * Set a validation flag for the provided element that is recognized by the validation service.
	 * Can be used to consider server side validations in the client side validation.
	 * Display server validation errors on the client side
	 */
	public setServerValidationBackendFlag(success: boolean, elementIdentifier: string, id: string): void {
		const $field = this.findValidatingElement(elementIdentifier);

		$field.data(BasicValidationService.VALIDATION_FAILED_MARKER, !success);
		if (success || id) {
			this.setValidationMarkers($field, success);
		}
		if (!success && (id && $field.length)) {
			let backendText = `Es ist ein Fehler aufgetreten (Fehlercode: ${id})`;
			if (this.options.app.validationmessages.backend[`code_${id}`]) {
				backendText = this.options.app.validationmessages.backend[`code_${id}`];
			}
			else {
				AppUtils.Helper.logError("", this.options.app.applicationId, window.location.href, `CMS: FehlerText Backend mit der Nummer ${id} fehlt`);
			}
			this.validators.forEach((validator) => {
				if (validator.lastActive && validator.lastActive.id === $field[0].id) {
					validator.element($field);
					validator.errorList[0].message = backendText;
				}
			});
			$field.parents(this.rowClass).find(this.errorClassSelector).html(backendText);
			this.scrollTo($field);
		}
	}

	public showErrorDialogWithMappedErrorMessage(success: boolean, elementIdentifier: string, id: string): void {
		const $field = this.findValidatingElement(elementIdentifier);

		if (!success && (id && $field.length)) {
			const backendText = this.getMappedBackendMessage(id);
			this.errorService.errorDialog(backendText);
		}
	}

	public getMappedBackendMessage(id: string): string {
		let backendText = `Es ist ein Fehler aufgetreten (Fehlercode: ${id})`;
		if (this.options.app.validationmessages.backend[`code_${id}`]) {
			backendText = this.options.app.validationmessages.backend[`code_${id}`];
		}
		else {
			AppUtils.Helper.logError("", this.options.app.applicationId, window.location.href, `CMS: FehlerText Backend mit der Nummer ${id} fehlt`);
		}
		return backendText;
	}

	protected findValidatingElement(elementIdentifier: string): JQuery {
		let $result = this.$ctx.find(`[data-validation="${elementIdentifier}"]`);

		if ($result.length === 0) {
			$result = this.$ctx.find(`#${elementIdentifier}`);
		}
		return $result;
	}

	/**
	 * Scroll to this item element
	 */
	/* istanbul ignore next: wrapper function */
	protected scrollTo(item: JQuery): void {
		AppUtils.View.scrollToElement(item, 100, 1000);
	}

	/**
	 * showErrors
	 *
	 * Show errors for certain fields (currently only for main *1st form)
	 */
	public showErrors(opts: { [key: string]: string }): void {
		if (this.forms.length && this.forms[0].validation) {
			this.forms[0].validation.showErrors(opts);
		}
	}

	protected addValidationMessages(): void {
		this.validators.forEach((validator) => {

			$(validator.currentForm).find('input, textarea, select').not(this.ignoreValidationSelector).each((key, element: HTMLInputElement) => {
				const validationRules = this.parseValidationAttr(element);
				if (validationRules && validationRules.length) {
					validationRules.forEach(validationRuleName => {
						if (!this.validationOptions.messages[validationRuleName]) {
							console.log(`No validation message found with data-validation name: ${validationRuleName}`);
						} else {
							if (!this.validationOptions.rules[validationRuleName]) {
								console.log(`No validation rule found with data-validation name: ${validationRuleName}`);
							} else {
								this.validationOptions.rules[validationRuleName].messages = this.validationOptions.messages[validationRuleName];
								$(element).rules('add', this.validationOptions.rules[validationRuleName]);
							}
						}
					});
				} else if (this.validationOptions.rules[element.name]) {
					if (!this.validationOptions.messages[element.name]) {
						console.log(`No validation message found with name: ${element.name}`);
					} else {
						this.validationOptions.rules[element.name].messages = this.validationOptions.messages[element.name];
						$(`[name="${element.name}"]`).each((index, other) => {
							$(other).rules('add', this.validationOptions.rules[element.name]);
						});
					}
				}
			});
			// revalidate datepicker fields on change
			$(validator.currentForm).find('.a-basic-input-text--calendar input').on("change", (e) => {
				$(this.forms)[0]['validation'].element($(e.target));
				this.decorateElement(e.target);
			});
		});
	}

	protected parseValidationAttr(element: HTMLElement | JQuery): string[] {
		const rawValue = $(element).data('validation') as string;
		if (rawValue) {
			return rawValue.split(",");
		}
		return [];
	}

	/**
	 * add custom validation methods
	 *
	 */
	protected addCustomMethods(): void {
		$.validator.addMethod('requireddatepast', this.validatorRequiredSameOrPastDate.bind(this));

		$.validator.addMethod('inputcross', this.validatorInputCross.bind(this));

		$.validator.addMethod('phonecross', this.validatorPhoneCross.bind(this));

		$.validator.addMethod('workphonecross', this.validatorWorkPhoneCross.bind(this));

		$.validator.addMethod('streetandnumber', this.validatorStreetAndNumber.bind(this));

		$.validator.addMethod('customdob', this.validatorCustomDoB.bind(this));

		$.validator.addMethod('custombegin', this.validatorCustomBegin.bind(this));

		$.validator.addMethod('monthyear', this.validatorMonthYear.bind(this));

		$.validator.addMethod('nonumber', this.validatorNoNumber.bind(this));

		$.validator.addMethod('notequals', this.validatorNotEquals.bind(this));

		$.validator.addMethod('requireddate', this.validatorRequiredDate.bind(this));

		$.validator.addMethod('zukunft', this.validatorFuture.bind(this));

		$.validator.addMethod('customflagvalidation', this.validatorCustomFlag.bind(this));

		$.validator.addMethod('password', this.validatorPassword.bind(this));

		$.validator.addMethod('calendarbegin', this.validatorCalendarBegin.bind(this));
		$.validator.addMethod('calendarend', this.validatorCalendarEnd.bind(this));


		$.validator.addMethod('equalTo', this.validatorEqualTo.bind(this));
		const equalTo = $.validator.methods['equalTo'];
		if (equalTo) {
			$.validator.methods['equalto'] = equalTo;
			$.validator.messages['equalto'] = $.validator.messages['equalTo'];
		}

		$.validator.addMethod('filerequired', this.validatorFileRequired.bind(this));
		$.validator.addMethod('email', this.validatorEmail.bind(this));

		$.validator.addMethod('oneoptinrequired', this.validatorOneOptinRequired.bind(this));
		$.validator.addMethod('starttomorrow', this.validatorStartTomorrow.bind(this));
		$.validator.addMethod('maxfutureoneyear', this.validatorFuterMaxOneYear.bind(this));

		$.validator.addMethod("oneleadingzero", this.validatorOneLeadingZero.bind(this));

		if (this.options.app.apicmglnumbercheck) {
			let mglNoMessage = "";
			$.validator.addMethod('membernumbercheck', (value, _element) => {
				let result: boolean;
				({ result: result, mglNoMessage } = this.validatorMemberNumberCheck(value, mglNoMessage));
				return result;
			}, () => mglNoMessage);

			$.validator.addMethod('membernumberwithvescheck', (value, _element) => {
				let result: boolean;
				({ result: result, mglNoMessage } = this.validatorMemberNumberWithVesCheck(value, mglNoMessage));
				return result;
			}, () => mglNoMessage);

			mglNoMessage = "";
			$.validator.addMethod('membernumbercheckoptional', (value, _element) => {
				let result = true;
				if (value !== '') {
					({ result: result, mglNoMessage } = this.validatorMemberNumberCheck(value, mglNoMessage));
				}
				return result;
			}, () => mglNoMessage);
		}
		// alias for require_from_group built-in
		const groupValidator = $.validator.methods['require_from_group'];
		if (groupValidator) {
			$.validator.methods['requirefromgroup'] = groupValidator;
			$.validator.messages['requirefromgroup'] = $.validator.messages['require_from_group'];
		}
	}

	protected validatorOneLeadingZero(value: any): boolean {
		return !/^0{2,}/.test(value);
	}

	protected validatorMemberNumberCheck(value: any, mglNoMessage: string): { result: boolean, mglNoMessage: string } {
		let isValid = false;
		AppUtils.Ajax.fragment({
			method: 'GET',
			async: false,
			headers: {
				'Ocp-Apim-Subscription-Key': T.Utils.Application.getApiMSubscriptionKey()
			},
			url: this.urlBuilder.fullApimUrl(this.options.app.apicmglnumbercheck),
			data: {
				'MglNo': value
			}
		}, (successData: ApiResponse<MglCheckExternResponseApi>) => {
			if (successData.Data.IsMglNo) {
				isValid = true;
			}
		}, (jqxhr: JQueryXHR) => {
			mglNoMessage = this.errorService.getErrorMessage(jqxhr.responseJSON.Messages[0]);
			isValid = false;
		});
		return { result: isValid, mglNoMessage };
	}

	protected validatorMemberNumberWithVesCheck(value: any, mglNoMessage: string): { result: boolean, mglNoMessage: string } {
		let isValid = false;
		AppUtils.Ajax.fragment({
			method: 'GET',
			async: false,
			url: this.urlBuilder.fullUrl(this.options.app.apicmglnumbercheck),
			data: {
				'MglNo': value
			}
		}, (successData: ApiResponse<MglVesCheckExternResponseApi>) => {
			if (successData.Data.IsMglOrVesNo) {
				isValid = true;
			}
		}, (jqxhr: JQueryXHR) => {
			mglNoMessage = this.errorService.getErrorMessage(jqxhr.responseJSON.Messages[0]);
			isValid = false;
		});
		return { result: isValid, mglNoMessage };
	}

	/**
	 * Email Validation in frontend as well as in backend
	 */
	protected validatorEmail(value: any, element: HTMLElement, _params: any): boolean {
		if ($(element).prop('required') || value.length > 0) {
			return T.Utils.Validation.emailPattern.test(value.trim());
		}
		return true;
	}

	/**
	 * Validate only on submit form
	 */
	/* istanbul ignore next: cannot emulate files */
	protected validatorFileRequired(value: any, element: HTMLInputElement, _params: any): boolean {
		if (this.isFormSubmit && element.files && element.files.length < 1) {
			return false;
		}
		return true;
	}

	protected validatorEqualTo(value: any, element: HTMLElement, params: any): boolean {
		// eslint-disable-next-line eqeqeq
		return value == $(params).val(); //NOSONAR // no type comparison for any
	}

	protected validatorPassword(value: any, element: HTMLElement, _params: any): boolean {
		if (element.id === 'Oldpassword') {
			return value.length > 0;
		}
		else {
			return (/^(?=.*\d)(?=.*[*²³!"#§€$%&'()*+,\-./:;<=>?@[\\\]^_`´{|}~])(?=.*[a-z])(?=.*[A-Z]).{8,}$/.test(value));
		}
	}

	/**
	 * Evaluates whether rage validation flag is set or not. Also see BasicViewService.setServerValidationFlag.
	 */
	protected validatorCustomFlag(value: any, element: HTMLElement, _params: any): boolean {
		return !($(element).data(BasicValidationService.VALIDATION_FAILED_MARKER));
	}

	/**
	 * add zukunft methode only for Inputpage. not for Calculator
	 */
	protected validatorFuture(value: any, element: HTMLElement, _params: any): boolean {
		// why both name and data-validation must be "Geburtsdatum"? this is breach of validation mappings
		// Check if the query must be used in the applications like this
		if ((element as HTMLInputElement).name === 'Geburtsdatum' || element.getAttribute('data-validation') === 'Geburtsdatum') {

			const date = AppUtils.Format.viewDateToIsoDate(value);
			if (date && date.length > 0) {
				return moment(date).isBefore(moment(), 'day');
			}
			return false;
		}
		return false;
	}

	/**
	 * workaround for datepicker values
	 */
	protected validatorRequiredDate(value: any, element: HTMLElement, params: any): boolean {
		// eslint-disable-next-line eqeqeq
		if (!value || value == "__.__.____") { //NOSONAR // no type comparison for any
			return false;
		}
		const rex = params && params.test ? params : /^(([1-9]|[0-2]\d|[3][0-1])\.([1-9]|[0]\d|[1][0-2])\.\d{4})$/;
		return (rex.test(value));
	}

	protected validatorNotEquals(value: any, element: HTMLElement, params: any): boolean {
		// eslint-disable-next-line eqeqeq
		return value != params; //NOSONAR // no type comparison for any
	}

	/**
	 * @deprecated use standard pattern
	 */
	protected validatorNoNumber(value: any, _element: HTMLElement, _params: any): boolean {
		return (/^[^0-9]*$/.test(value));
	}

	protected validatorMonthYear(value: any, _element: HTMLElement, _params: any): boolean {
		return (/(0[1-9]|1[012]).[0-9]{4}/.test(value) || /[0-9]{4}-(0[1-9]|1[012])/.test(value));
	}

	/**
	 * @deprecated use validatorRequiredDate
	 */
	/* istanbul ignore next: obsolete function */
	protected validatorCustomBegin(value: any, _element: HTMLElement, _params: any): boolean {
		return (/(0[1-9]|1[0-9]|2[0-9]|3[01]).(0[1-9]|1[012]).[0-9]{4}/.test(value) || /[0-9]{4}-(0[1-9]|1[012])-(0[1-9]|1[0-9]|2[0-9]|3[01])/.test(value));
	}

	/**
	 * @deprecated use validatorRequiredDate
	 */
	/* istanbul ignore next: obsolete function */
	protected validatorCustomDoB(value: any, element: HTMLElement, params: any): boolean {
		return this.validatorCustomBegin(value, element, params);
	}

	protected validatorStreetAndNumber(value: any, _element: HTMLElement, _params: any): boolean {
		if (!value) {
			return false;
		}
		return (/^[a-zA-Z0-9àáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð ,.'-/]+$/.test(value));
	}

	/**
	 * @deprecated use standard required_from_group (or alias requirefromgroup)
	 */
	/* istanbul ignore next: obsolete function */
	protected validatorWorkPhoneCross(value: any, element: HTMLElement, _params: any): boolean {
		return this.validatePhoneNumberCross(element, "TelefonGeschaeftlichVorwahl", "TelefonGeschaeftlichNummer");
	}

	/**
	 * @deprecated use standard required_from_group (or alias requirefromgroup)
	 */
	/* istanbul ignore next: obsolete function */
	protected validatorPhoneCross(value: any, element: HTMLElement, _params: any): boolean {
		return this.validatePhoneNumberCross(element, "TelefonVorwahl", "TelefonNummer");
	}

	/**
	 * @deprecated use standard required_from_group (or alias requirefromgroup)
	 */
	/* istanbul ignore next: obsolete function */
	protected validatePhoneNumberCross(element: HTMLElement, prefixFieldName: string, suffixFieldName: string): boolean {
		element['was_seen'] = true;
		const name = $(element).attr('name'),
			$prefix = $(`input[name="${name?.replace(suffixFieldName, prefixFieldName)}"]`),
			$suffix = $(`input[name="${name?.replace(prefixFieldName, suffixFieldName)}"]`);
		if ($prefix[0]['was_seen'] && $suffix[0]['was_seen']) {
			if (AppUtils.Helper.isEmptyString($prefix.val()) && AppUtils.Helper.isEmptyString($suffix.val())) {
				return true;
			}
			$prefix.removeClass('is-error');
			$suffix.removeClass('is-error');
			return !AppUtils.Helper.isEmptyString($prefix.val()) && !AppUtils.Helper.isEmptyString($suffix.val());
		}
		return true;
	}

	/**
	 * @deprecated use standard required_from_group (or alias requirefromgroup)
	 */
	/* istanbul ignore next: obsolete function */
	protected validatorInputCross(value: any, element: HTMLElement, params: any): boolean {
		if (params.length <= 0) {
			console.log('dependency is not set');
			return false;
		}
		element['was_seen'] = true;
		const $element = $(element), $dependency = $element.closest('fieldset').find(`input[data-validation="${params[0]}"]`);
		if ($element[0]['was_seen'] && $dependency[0]['was_seen']) {
			if (AppUtils.Helper.isEmptyString($element.val()) && AppUtils.Helper.isEmptyString($dependency.val())) {
				return true;
			}
			$element.removeClass('is-error');
			$dependency.removeClass('is-error');
			return !AppUtils.Helper.isEmptyString($element.val()) && !AppUtils.Helper.isEmptyString($dependency.val());
		}
		return true;
	}

	protected validatorRequiredSameOrPastDate(value: any): boolean {
		const date = AppUtils.Format.viewDateToIsoDate(value);
		if (date && date.length > 0) {
			return moment(date).isSame(moment()) ||
				(moment(date).isBefore(moment()));
		}
		return false;
	}

	protected validatorCalendarBegin(value: any, element: HTMLElement, _params: any): boolean {
		if (value && value.toString().length && "__.__.____" !== value) {
			const calConf: JQuery.PlainObject = $(element).parent(this.calendarSelector).data('init');
			if (calConf && calConf.begin) {
				const d = moment(AppUtils.Format.viewDateToIsoDate(value));
				return d.isSameOrAfter(AppUtils.Format.parseDateOffset(calConf.begin), 'days');
			}
		}
		return true;
	}

	protected validatorCalendarEnd(value: any, element: HTMLElement, _params: any): boolean {
		if (value && value.toString().length && "__.__.____" !== value) {
			const calConf: JQuery.PlainObject = $(element).parent(this.calendarSelector).data('init');
			if (calConf && calConf.end) {
				const d = moment(AppUtils.Format.viewDateToIsoDate(value));
				return d.isSameOrBefore(AppUtils.Format.parseDateOffset(calConf.end), 'days');
			}
		}
		return true;
	}

	/**
	 * @deprecated use standard required_from_group (or alias requirefromgroup)
	 */
	/* istanbul ignore next: obsolete function */
	protected validatorOneOptinRequired(value: any, element: HTMLElement, _params: any): boolean {
		if (value && (document.body as any).isFormSubmit) {
			const $element = $(element);
			return $element.closest(this.rowClass).first().find(".js-email:checked, .js-phone:checked, .js-personalization:checked").length > 0;
		}
		return true;
	}

	protected validatorStartTomorrow(value: any): boolean {
		const tomorrow = moment().add(1, 'days');
		return moment(AppUtils.Format.viewDateToIsoDate(value)).isSameOrAfter(tomorrow, 'day');
	}

	protected validatorFuterMaxOneYear(value: any): boolean {
		const futureYear = moment().add(1, 'years');
		return moment(AppUtils.Format.viewDateToIsoDate(value)).isSameOrBefore(futureYear, 'day');
	}

	public validatorCompleteAddress(address: { street?: string, number?: string, zip: string, city: string, streetnumber?: string }): boolean {
		const isFilled = (value?: string): boolean => value?.trim().length > 0;

		const hasSeparateStreetAndNumber = isFilled(address.street) && isFilled(address.number);
		const hasCombinedStreetNumber = isFilled(address.streetnumber);

		return (hasSeparateStreetAndNumber || hasCombinedStreetNumber) && isFilled(address.zip) && isFilled(address.city);
	}

	/**
	 * trigger validation manually from caller
	 * Called with the parameter elemArray, it triggers only the elments in the array.
	 * Called without the parameter, it triggers the whole form.
	 * It triggers only the current form!
	 *
	 * @param elemArray array of strings. Entries are values from input, textarea, select referenced in name oder data-validation attribute
	 */
	public triggerValidation(elemArray?: string[]): void {
		$.each(this.validators, (key, validator) => {
			if (elemArray && elemArray.length > 0) {
				elemArray.forEach((elementId) => {
					if (validator && validator.currentForm) {
						let $elem = $(validator.currentForm).find(`[data-validation="${elementId}"]`);
						if (!$elem.length) {
							$elem = $(validator.currentForm).find(`#${elementId}`);
						}
						if ($elem.length) {
							validator.element($elem);
						}
					}
				});
			} else {
				$.each($(validator.currentForm).find('input, select, textarea'), (k, elementItem) => {
					const $elem = $(elementItem);
					validator.element($elem);
				});
			}
		});
	}

	public isElementValid($element: JQuery, allowEmpty = false, ruleName?: string): boolean {
		let currentValidator: any;
		$.each(this.validators, (key, validator) => {
			if ($(validator.currentForm).find($element).length) {
				currentValidator = validator;
			}
		});

		if (!currentValidator) {
			return true;
		}

		const value = $element.val();
		if (allowEmpty && (value === null || value === undefined || value === '')) {
			return true;
		}

		const rules = $($element).rules();
		let isValid = true;

		if (ruleName) {
			const method = $.validator.methods[ruleName];
			const param = rules[ruleName];
			if (method) {
				const result = method.call(currentValidator, value, $element[0], param);
				if (!result) {
					isValid = false;
				}
			}
		} else {
			$.each(rules, (rule: string, param: any) => {
				const method = $.validator.methods[rule];
				if (method) {
					const result = method.call(currentValidator, value, $element[0], param);
					if (!result) {
						isValid = false;
					}
				}
			});
		}

		return isValid;
	}

	public isSelectorValid(selector: string, allowEmpty = false, ruleName?: string): boolean {
		const elements = $(selector).toArray();

		for (const element of elements) {
			const $element = $(element);
			if (!this.isElementValid($element, allowEmpty, ruleName)) {
				return false;
			}
		}

		return true;
	}

	public addValidationForElement(element: JQuery | HTMLElement, validationRulesAndMessages: JQueryValidation.RulesDictionary): void {
		$(element).rules('add', validationRulesAndMessages);
	}

	public removeValidationForElement(element: JQuery | HTMLElement, validationRuleNames: string): void {
		$(element).rules('remove', validationRuleNames);
	}

	public getValidationMessageObjectByName(name: string): { [key: string]: any } {
		let validationObject = {};
		if (this.options.app.validationmessages && this.options.app.validationmessages.client && this.options.app.validationmessages.client[name] !== undefined) {
			validationObject = this.options.app.validationmessages.client[name];
		}
		return validationObject;
	}

	/**
	 * Lookup map for Serverfieldnames and field ids
	 * key: serverfieldname
	 * value: field id
	 */
	protected fillLookupServerFieldnames(): void {

		this.lookupServerFieldnames["Anrede"] = "salutation_1";
		this.lookupServerFieldnames["Nachname"] = "lastname";
		this.lookupServerFieldnames["Vorname"] = "firstname";
		this.lookupServerFieldnames["Firma"] = "street";
		this.lookupServerFieldnames["Beguenstigter"] = "company_addition";
		this.lookupServerFieldnames["Geburtsdatum"] = "dateofbirth";

		this.lookupServerFieldnames["Anschrift.StrasseHausnummer"] = "streetandno";
		this.lookupServerFieldnames["Anschrift.Postleitzahl"] = "zip";
		this.lookupServerFieldnames["Anschrift.Ort"] = "city";
		this.lookupServerFieldnames["Anschrift.Ergaenzungszeile"] = "Ergaenzungszeile";

		this.lookupServerFieldnames["Telefon.Prefix"] = "phone_prefix";
		this.lookupServerFieldnames["Telefon.Suffix"] = "phone_suffix";
		this.lookupServerFieldnames["TelGesch.Prefix"] = "phone_work_prefix";
		this.lookupServerFieldnames["TelGesch.Suffix"] = "phone_work_suffix";
		this.lookupServerFieldnames["Email"] = "email";

		this.lookupServerFieldnames["Iban"] = "Bankdaten.Iban";
		this.lookupServerFieldnames["Bic"] = "Bankdaten.Bic";
		this.lookupServerFieldnames["Bankname"] = "Bankdaten.Name";
		this.lookupServerFieldnames["Bankleitzahl"] = "Bankleitzahl";
		this.lookupServerFieldnames["Kontonummer"] = "Kontonummer";
		this.lookupServerFieldnames["AbwKontoInhaber.AnredeKennzeichen"] = "Bankdaten\\.AbwKontoInhaber\\.AnredeKennzeichen_1";
		this.lookupServerFieldnames["AbwKontoInhaber.Vorname"] = "Bankdaten\\.AbwKontoInhaber\\.Vorname";
		this.lookupServerFieldnames["AbwKontoInhaber.Name"] = "Bankdaten\\.AbwKontoInhaber\\.Name";
		this.lookupServerFieldnames["AbwKontoInhaber.Adresse.StrasseHausnummer"] = "Bankdaten\\.AbwKontoInhaber\\.Adresse\\.StrasseHausnummer";
		this.lookupServerFieldnames["AbwKontoInhaber.Adresse.Postleitzahl"] = "Bankdaten\\.AbwKontoInhaber\\.Adresse\\.Postleitzahl";
		this.lookupServerFieldnames["AbwKontoInhaber.Adresse.Ort"] = "Bankdaten\\.AbwKontoInhaber\\.Adresse\\.Ort";
		this.lookupServerFieldnames["AbwKontoInhaber.Adresse.Ergaenzungszeile"] = "Bankdaten\\.AbwKontoInhaber\\.Adresse\\.Ergaenzungszeile";

		this.lookupServerFieldnames["VersicherterVorname"] = "insuredperson_firstname";
		this.lookupServerFieldnames["VersicherterNachname"] = "insuredperson_lastname";
		this.lookupServerFieldnames["VersicherterOrganisation"] = "VersicherterOrganisation";
		this.lookupServerFieldnames["Identity"] = "Oldpassword";
		this.lookupServerFieldnames["Password"] = "change-password-new";
		this.lookupServerFieldnames["LoginMail"] = "personal-data-email";
	}
}

